import {
  MyVaroSectionTile,
  MyVaroVxlTile,
  Query,
  RouteNavigationAction,
  TileType,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import MyVaroMenuTile from 'services/graphql/queries/MyVaroMenuTile';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';
import { AnyVariables, useQuery } from 'urql';

const ManageAccounts = () => {
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();

  const [
    {
      data: myVaroMenuData,
      fetching: myVaroMenuFetching,
      error: myVaroMenuError,
    },
    refresh,
  ] = useQuery<Query, AnyVariables>({
    query: MyVaroMenuTile,
    requestPolicy: 'cache-and-network',
    variables: {
      id: 'MY_VARO_MANAGE_ACCOUNTS',
    },
  });

  const handleTileOnPress = (action: Maybe<RouteNavigationAction>) => {
    if (action?.route) {
      router.push(action.route as Href);
    }
    if (action?.analyticsEvent) {
      const { name, properties } = parseGQLAnalyticsEvent(
        action.analyticsEvent,
      );
      void segmentClient.logEvent(name, properties);
    }
  };

  const returnTile: VXLNode[] = [];
  myVaroMenuData?.myVaroMenuTile?.tiles?.forEach((tile: MyVaroSectionTile) => {
    if (tile.tileType === TileType.MyVaroSectionTile) {
      tile?.tiles?.forEach((subTile: MyVaroVxlTile) => {
        const content =
          subTile?.content?.__typename === Typenames.VxlListItem
            ? subTile.content
            : undefined;

        const vxlListItemTrailingAccessory = content?.trailingAccessory;
        const trailingAccessory =
          vxlListItemTrailingAccessory?.__typename === Typenames.Badge
            ? {
                id: vxlListItemTrailingAccessory.text,
                name: VXLComponentId.BADGE,
                props: {
                  badgeValue: Number(vxlListItemTrailingAccessory.text),
                },
              }
            : {};

        returnTile.push({
          id: subTile.id,
          name: VXLComponentId.LIST_ITEM,
          props: {
            testID: subTile.id,
            primaryCopy: content?.primaryText,
            secondaryCopy: content?.secondaryText,
            showsChevron: content?.showsChevron,
            onPress: () =>
              handleTileOnPress(
                content?.onPressAction as RouteNavigationAction,
              ),
            trailingAccessory,
          },
        });
      });
    }
  });

  const structuredData = {
    sectionList: {
      sections: [
        {
          sectionHeader: undefined,
          sectionFooter: undefined,
          data: returnTile,
        },
      ],
    },
  };

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'manageAccounts',
  });

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(myVaroMenuError);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: () => refresh(),
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: () => router.back(),
    },
  };

  const errorModel = myVaroMenuError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  useFocusEffect(
    useCallback(() => {
      refresh();
    }, [refresh]),
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('titleTabapay'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <SectionedContentScreen
        initialNumToRender={25}
        model={structuredData.sectionList ?? { sections: [] }}
        refreshing={myVaroMenuFetching}
        onRefresh={() => refresh()}
        testID="ManageAccounts"
      />
      <ScreenTransitionOverlay
        isLoading={myVaroMenuFetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ManageAccounts;
